<template>
  <div class="suppliers-view">
    <PageHeader title="Leveranciers" description="Beheer je leveranciers voor producten en orders">
      <template #actions>
        <Btn
          variant="outlined"
          @click="$router.push('/settings/suppliers/catalog')"
          icon="package"
        >
          Leverancier catalogus
        </Btn>
        <Btn variant="primary" @click="handleAddSupplier" icon="plus">Leverancier toevoegen</Btn>
      </template>
    </PageHeader>

    <div class="suppliers-view__content">
      <EmptyState
        v-if="!loading && suppliers.length === 0"
        title="Nog geen leveranciers"
        description="Voeg je eerste leverancier toe om producten aan te koppelen"
        icon="package"
      >
        <Btn variant="secondary" @click="handleAddSupplier" icon="plus">Leverancier toevoegen</Btn>
      </EmptyState>

      <div v-else-if="loading" class="suppliers-view__loading">
        <span>Laden...</span>
      </div>

      <div v-else class="suppliers-view__list">
        <div
          v-for="supplier in suppliers"
          :key="supplier.id"
          class="supplier-card"
          @click="handleEditSupplier(supplier)"
        >
          <div class="supplier-card__header">
            <div class="supplier-card__icon">
              <BaseIcon name="package" size="20" />
            </div>
            <div class="supplier-card__info">
              <h3 class="supplier-card__name">{{ supplier.name }}</h3>
              <span class="supplier-card__contact" v-if="supplier.contact_person">
                {{ supplier.contact_person }}
              </span>
            </div>
            <div class="supplier-card__status">
              <span
                v-if="supplier.catalog_id"
                class="status-badge status-badge--catalog"
              >
                Catalogus
              </span>
              <span
                class="status-badge"
                :class="{
                  'status-badge--active': supplier.active,
                  'status-badge--inactive': !supplier.active,
                }"
              >
                {{ supplier.active ? 'Actief' : 'Inactief' }}
              </span>
            </div>
          </div>

          <div class="supplier-card__details">
            <div class="supplier-card__detail" v-if="supplier.email">
              <BaseIcon name="mail" size="14" />
              <span>{{ supplier.email }}</span>
            </div>
            <div class="supplier-card__detail" v-if="supplier.phone">
              <BaseIcon name="phone" size="14" />
              <span>{{ supplier.phone }}</span>
            </div>
            <div class="supplier-card__detail" v-if="supplier.export_method">
              <BaseIcon name="send" size="14" />
              <span>{{ getExportMethodLabel(supplier.export_method) }}</span>
            </div>
          </div>

          <div class="supplier-card__meta">
            <span class="supplier-card__delivery" v-if="supplier.default_delivery_days">
              Levertijd: {{ supplier.default_delivery_days }} dagen
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, onMounted } from 'vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import CreateEditSupplierModal from '@/components/suppliers/CreateEditSupplierModal.vue';
  import { supplierService } from '@/services/supplierService';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';

  export default {
    name: 'SettingsSuppliersView',

    components: {
      PageHeader,
      Btn,
      BaseIcon,
      EmptyState,
    },

    setup() {
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const suppliers = ref([]);
      const loading = ref(true);

      const fetchSuppliers = async () => {
        try {
          loading.value = true;
          const response = await supplierService.getAllSuppliers({ limit: 100 });
          suppliers.value = response.suppliers || response.data || [];
        } catch (error) {
          console.error('Failed to fetch suppliers:', error);
          toastStore.addToast({
            message: 'Kon leveranciers niet laden',
            type: 'error',
          });
        } finally {
          loading.value = false;
        }
      };

      const getExportMethodLabel = (method) => {
        const labels = {
          webhook: 'Webhook (Make/Zapier)',
          template: 'Custom Template',
          email: 'E-mail',
          manual: 'Handmatig',
        };
        return labels[method] || method;
      };

      const handleAddSupplier = () => {
        const modalId = uiStore.addModal(CreateEditSupplierModal, {
          title: 'Leverancier toevoegen',
          supplier: null,
          onSubmit: async (supplierData) => {
            try {
              await supplierService.createSupplier(supplierData);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: 'Leverancier succesvol toegevoegd',
                type: 'success',
              });
              fetchSuppliers();
            } catch (error) {
              console.error('Failed to create supplier:', error);
              toastStore.addToast({
                message: 'Kon leverancier niet toevoegen',
                type: 'error',
              });
            }
          },
          onError: (errors) => {
            console.error('Validation errors:', errors);
          },
        });
      };

      const handleEditSupplier = (supplier) => {
        const modalId = uiStore.addModal(CreateEditSupplierModal, {
          title: 'Leverancier bewerken',
          supplier: supplier,
          onSubmit: async (supplierData) => {
            try {
              await supplierService.updateSupplier(supplier.id, supplierData);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: 'Leverancier succesvol bijgewerkt',
                type: 'success',
              });
              fetchSuppliers();
            } catch (error) {
              console.error('Failed to update supplier:', error);
              toastStore.addToast({
                message: 'Kon leverancier niet bijwerken',
                type: 'error',
              });
            }
          },
          onDelete: async () => {
            if (confirm('Weet je zeker dat je deze leverancier wilt verwijderen?')) {
              try {
                await supplierService.deleteSupplier(supplier.id);
                uiStore.closeModal(modalId);
                toastStore.addToast({
                  message: 'Leverancier verwijderd',
                  type: 'success',
                });
                fetchSuppliers();
              } catch (error) {
                console.error('Failed to delete supplier:', error);
                toastStore.addToast({
                  message: 'Kon leverancier niet verwijderen',
                  type: 'error',
                });
              }
            }
          },
          onError: (errors) => {
            console.error('Validation errors:', errors);
          },
        });
      };

      onMounted(() => {
        fetchSuppliers();
      });

      return {
        suppliers,
        loading,
        getExportMethodLabel,
        handleAddSupplier,
        handleEditSupplier,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';

  .suppliers-view {
    &__content {
      margin-top: 1.5rem;
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 3rem;
      color: var(--slate-500);
    }

    &__list {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;

      @include lg-down {
        grid-template-columns: 1fr;
      }
    }
  }

  .supplier-card {
    background: white;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 1.25rem;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--slate-300);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    &__header {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      margin-bottom: 1rem;
    }

    &__icon {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--slate-100);
      border-radius: 0.5rem;
      color: var(--slate-600);
    }

    &__info {
      flex: 1;
      min-width: 0;
    }

    &__name {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__contact {
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__status {
      flex-shrink: 0;
    }

    &__details {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      margin-bottom: 0.75rem;
    }

    &__detail {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.8125rem;
      color: var(--slate-600);

      svg {
        color: var(--slate-400);
      }
    }

    &__meta {
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-100);
    }

    &__delivery {
      font-size: 0.75rem;
      color: var(--slate-500);
    }
  }

  .status-badge {
    display: inline-flex;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;

    &--active {
      background: var(--green-100);
      color: var(--green-700);
    }

    &--inactive {
      background: var(--slate-100);
      color: var(--slate-600);
    }

    &--catalog {
      background: var(--brand-50);
      color: var(--brand-500);
    }
  }
</style>
